import React, { RefObject, ReactNode } from 'react';

export interface ScrollableMessageAreaProps {
  /**
   * Ref for the outer scroll container
   */
  containerRef: RefObject<HTMLDivElement>;

  /**
   * Ref for the scroll anchor element (used for auto-scroll)
   */
  scrollAnchorRef: RefObject<HTMLDivElement>;

  /**
   * Children to render inside the scrollable area
   */
  children: ReactNode;

  /**
   * Additional CSS classes for the inner content area
   * @default undefined
   */
  className?: string;

  /**
   * Bottom padding to allow content to scroll above the input
   * @default ''
   */
  bottomPadding?: string;
}

/**
 * Reusable scrollable message area component
 *
 * Provides a consistent scroll container structure with flex-reverse layout
 * for chat messages that grow from the bottom up.
 *
 * @example
 * const { containerRef, scrollAnchorRef } = useAutoScroll({...});
 *
 * <ScrollableMessageArea
 *   containerRef={containerRef}
 *   scrollAnchorRef={scrollAnchorRef}
 * >
 *   <AIChatHistory />
 * </ScrollableMessageArea>
 */
export const ScrollableMessageArea: React.FC<ScrollableMessageAreaProps> = ({
  containerRef,
  scrollAnchorRef,
  children,
  className,
  bottomPadding = '',
}) => {
  return (
    <div
      className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col h-full relative"
      ref={containerRef}
    >
      <div
        className={`flex-1 overflow-y-auto overflow-x-hidden flex flex-col ${className || ''}`}
        role="log"
      >
        <div className={`px-3 pt-2 ${bottomPadding}`}>{children}</div>
        <div ref={scrollAnchorRef} />
      </div>
    </div>
  );
};
